import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { User, UserModel } from './user.interface';

type ReadonlyAuthUser = Readonly<User>;

@Injectable({ providedIn: 'root' })
export class AuthStore {
    private static readonly TOKEN_STORAGE_KEY = 'auth_token';
    private readonly platformId = inject(PLATFORM_ID);

    private readonly _user = signal<User | null>(null);
    private readonly _token = signal<string | null>(this.readStoredToken());

    readonly user = computed<ReadonlyAuthUser | null>(() => {
        const currentUser = this._user();

        if (!currentUser) {
            return null;
        }

        return Object.freeze(structuredClone(currentUser));
    });

    readonly userModel = computed<UserModel | null>(() => {
        const currentUser = this._user();
        return currentUser ? new UserModel(currentUser) : null;
    });

    readonly token = computed(() => this._token());

    readonly isAuthenticated = computed(() => this._user() !== null && this._token() !== null);
    readonly isAdmin = computed(() => this.userModel()?.isAdmin() ?? false);

    can(permission: string | string[]): boolean {
        const userModel = this.userModel();

        if (!userModel) {
            return false;
        }

        const permissions = Array.isArray(permission) ? permission : [permission];

        return permissions.some((permissionName) => userModel.hasPermission(permissionName));
    }

    setSession(user: User, token: string): void {
        const normalizedToken = token.trim();

        this._user.set(structuredClone(user));
        this._token.set(normalizedToken || null);

        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if (normalizedToken) {
            localStorage.setItem(AuthStore.TOKEN_STORAGE_KEY, normalizedToken);
            return;
        }

        localStorage.removeItem(AuthStore.TOKEN_STORAGE_KEY);
    }

    clear(): void {
        this._user.set(null);
        this._token.set(null);

        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        localStorage.removeItem(AuthStore.TOKEN_STORAGE_KEY);
    }

    private readStoredToken(): string | null {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        const storedToken = localStorage.getItem(AuthStore.TOKEN_STORAGE_KEY);
        const normalizedToken = storedToken?.trim();

        return normalizedToken ? normalizedToken : null;
    }
}
